/**
 * The run state of a step, and every way of waiting on it.
 *
 * A step that can be paused and stopped needs three things at once: somewhere
 * to hold which of the three states it is in, a way for the play loop to be
 * held at a phase boundary, and a way for a dwell to consume only the time the
 * step actually spent running. Spreading those across a set of booleans and
 * ad-hoc promises makes invalid combinations representable — paused *and*
 * stopped, or a waiter woken while the flags say something else — and leaves
 * every reader to re-derive the state after waking.
 *
 * Holding it in one place makes the transitions single assignments and gives
 * the waiters one thing to consult.
 */
export type RunState = 'running' | 'paused' | 'stopped';

/** A wait that can be abandoned, so nothing outlives the race it entered. */
interface Cancellable<T> {
  promise: Promise<T>;
  cancel: () => void;
}

export class RunGate {
  private state: RunState = 'running';

  /**
   * Resolvers waiting to hear that `state` changed. Woken and cleared on every
   * transition; a waiter that loses a race removes its own resolver rather
   * than leaving it to accumulate.
   */
  private waiters = new Set<() => void>();

  public get isRunning(): boolean {
    return this.state === 'running';
  }

  public get isPaused(): boolean {
    return this.state === 'paused';
  }

  public get isStopped(): boolean {
    return this.state === 'stopped';
  }

  /**
   * Stop is terminal: a resume arriving after it must not restart anything,
   * and a pause after it must not un-stop the step. Everything else is a plain
   * assignment plus a wake.
   */
  public pause(): void {
    this.transition('paused');
  }

  public resume(): void {
    this.transition('running');
  }

  public stop(): void {
    this.transition('stopped');
  }

  /**
   * Back to running for a fresh play.
   *
   * A step instance is replayed by a loop, so the terminal stop of the
   * previous iteration has to be cleared deliberately — which is the one place
   * that is allowed to move out of `stopped`.
   */
  public reset(): void {
    this.state = 'running';
    this.wake();
  }

  private transition(next: RunState): void {
    if (this.state === 'stopped' || this.state === next) {
      return;
    }

    this.state = next;
    this.wake();
  }

  private wake(): void {
    const woken = [...this.waiters];

    this.waiters.clear();

    for (const resolve of woken) {
      resolve();
    }
  }

  /** Resolves on the next transition, or when the caller gives up. */
  private changed(): Cancellable<void> {
    let resolve!: () => void;

    const promise = new Promise<void>((r) => {
      resolve = r;
    });

    this.waiters.add(resolve);

    return {
      promise,
      cancel: () => {
        this.waiters.delete(resolve);
        resolve();
      },
    };
  }

  /**
   * Hold here while paused.
   *
   * This is the phase boundary: the question every caller is really asking is
   * "may the next thing happen?", and pause and stop are both answers of no —
   * one temporary, one final.
   */
  public async whenRunning(): Promise<'running' | 'stopped'> {
    if (this.state !== 'paused') {
      return this.state === 'stopped' ? 'stopped' : 'running';
    }

    await this.changed().promise;

    return this.whenRunning();
  }

  /**
   * Wait `ms` of *running* time: a pause suspends the countdown rather than
   * letting it drain while the step is parked.
   *
   * @returns whether the time elapsed with the step still running.
   */
  public async wait(ms: number): Promise<boolean> {
    const countdown = this.countRunningTime(ms);

    const outcome = await countdown.promise;

    if (outcome === 'stopped') {
      return false;
    }

    // The timer can settle before a transition wakes the countdown, so running
    // out is not on its own permission to carry on.
    return (await this.whenRunning()) === 'running';
  }

  /**
   * Race `contender` against a stop, and optionally against a deadline that
   * counts running time.
   *
   * The deadline lives here rather than in a second implementation so that it
   * shares the one definition of "time the step was running", and so that it
   * is cancelled when the race settles — a bare timer left in a `Promise.race`
   * stays pending for its full duration every time it loses.
   */
  public async race<T>(
    contender: Promise<T>,
    { timeoutMs }: { timeoutMs?: number } = {},
  ): Promise<T | 'stopped' | 'timedOut'> {
    const stopped = this.whenStopped();

    const deadline =
      timeoutMs == null ? undefined : this.countRunningTime(timeoutMs);

    try {
      return await Promise.race([
        contender,
        stopped.promise,
        ...(deadline == null
          ? []
          : [
              deadline.promise.then((outcome) =>
                outcome === 'elapsed'
                  ? ('timedOut' as const)
                  : ('stopped' as const),
              ),
            ]),
      ]);
    } finally {
      stopped.cancel();
      deadline?.cancel();
    }
  }

  private whenStopped(): Cancellable<'stopped'> {
    let abandoned = false;
    let release: (() => void) | undefined;

    const watch = async (): Promise<'stopped'> => {
      if (abandoned || this.state === 'stopped') {
        // Abandoned watchers never win the race they were entered into, so
        // the value here is only ever read when a stop really happened.
        return 'stopped';
      }

      const change = this.changed();

      release = change.cancel;

      await change.promise;

      release = undefined;

      return watch();
    };

    return {
      promise: watch(),
      cancel: () => {
        abandoned = true;
        release?.();
      },
    };
  }

  /**
   * Burn `ms` of running time, waking early whenever the state changes so that
   * a pause stops the clock at the moment it was asked for rather than at the
   * end of the current sleep.
   */
  private countRunningTime(
    ms: number,
  ): Cancellable<'elapsed' | 'stopped' | 'abandoned'> {
    let abandoned = false;
    let release: (() => void) | undefined;

    // Held out here so `cancel` can drop it in the same tick. Waiting for the
    // suspended race below to unwind would leave the timer armed for a
    // microtask after the caller believes it is gone, which is exactly the
    // guarantee a per-weld deadline needs to be able to lean on.
    let timer: ReturnType<typeof setTimeout> | undefined;

    /**
     * One slice of the countdown, then itself with whatever is left.
     *
     * `remaining` is a parameter rather than a mutated local, so the span the
     * timer is armed for is the span this call was given and cannot be
     * rewritten under it.
     */
    const run = async (
      remaining: number,
    ): Promise<'elapsed' | 'stopped' | 'abandoned'> => {
      // Guard order carried over from the loop this replaces: a countdown
      // with nothing left reports 'elapsed' before either interruption is
      // consulted, so a zero-length span answers the same on a gate that has
      // already been abandoned or stopped.
      if (remaining <= 0) {
        return 'elapsed';
      }

      if (abandoned) {
        return 'abandoned';
      }

      if (this.state === 'stopped') {
        return 'stopped';
      }

      if (this.state === 'paused') {
        if ((await this.whenRunning()) === 'stopped') {
          return 'stopped';
        }

        return run(remaining);
      }

      const startedAt = performance.now();
      const change = this.changed();

      release = change.cancel;

      let fire!: () => void;

      const elapsed = new Promise<'elapsed'>((resolve) => {
        fire = () => resolve('elapsed');
      });

      timer = setTimeout(fire, remaining);

      const outcome = await Promise.race([
        elapsed,
        change.promise.then(() => 'changed' as const),
      ]);

      clearTimeout(timer);
      timer = undefined;
      change.cancel();
      release = undefined;

      if (outcome === 'elapsed') {
        return 'elapsed';
      }

      // Only the time actually spent running comes off the clock, and it is
      // read off the monotonic clock rather than the wall clock: a box that
      // boots with a stale time and syncs mid-weld would otherwise have the
      // NTP step land in this subtraction, and the error is the size of the
      // step — backwards, the arc keeps burning past the configured duration
      // with no cap to catch it.
      return run(remaining - (performance.now() - startedAt));
    };

    return {
      promise: run(ms),
      cancel: () => {
        abandoned = true;
        clearTimeout(timer);
        timer = undefined;
        release?.();
      },
    };
  }
}
